import React, { useState, useEffect } from "react";
import { t } from "i18next";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Badge,
  Alert,
} from "reactstrap";
import { useStaffStore } from "../../../stores/Staffs";
import { tokenHelper } from "../../../stores/helpers";
import styled from "styled-components";

const EditPermissions = ({ showPermissionsModal, togglePermissionsModal, rowData }) => {
  const { updateUserPermissions, isLoading } = useStaffStore((state) => state);
  const [isRoot, setIsRoot] = useState(false);
  const [permissions, setPermissions] = useState({
    kitchen: 0,
    site: 0,
    space: 0,
    menu: 0,
    menu_item: 0,
    device: 0,
    order: 0,
    delivery_time_report: 0,
    service_station_report: 0,
    feedback: 0,
    link: 0,
    setting: 0,
    staff: 0,
    announcements: 0,
    mymenus: 0,
    myspaces: 0,
  });

  // Features with their display names and descriptions
  const features = [
    {
      key: "kitchen",
      name: "Kitchen",
      icon: "ri-restaurant-2-line",
      description: "Manage kitchens and kitchen staff",
    },
    {
      key: "site",
      name: "Site",
      icon: "ri-building-2-line",
      description: "Manage sites and locations",
    },
    {
      key: "space",
      name: "Space",
      icon: "ri-layout-grid-line",
      description: "Manage spaces and tables",
    },
    {
      key: "menu",
      name: "Menu",
      icon: "ri-file-list-3-line",
      description: "Manage menus and menu items",
    },
    {
      key: "menu_item",
      name: "Menu Item",
      icon: "ri-restaurant-line",
      description: "Manage individual menu items",
    },
    {
      key: "device",
      name: "Device",
      icon: "ri-device-line",
      description: "Manage devices and hardware",
    },
    {
      key: "order",
      name: "Order",
      icon: "ri-shopping-cart-line",
      description: "View and manage orders",
    },
    {
      key: "delivery_time_report",
      name: "Delivery Time Report",
      icon: "ri-time-line",
      description: "View delivery time analytics",
    },
    {
      key: "service_station_report",
      name: "Service Station Report",
      icon: "ri-bar-chart-line",
      description: "View service station analytics",
    },
    {
      key: "feedback",
      name: "Feedback",
      icon: "ri-feedback-line",
      description: "View and manage customer feedback",
    },
    {
      key: "link",
      name: "Link",
      icon: "ri-link",
      description: "Manage QR codes and links",
    },
    {
      key: "announcements",
      name: "Announcements",
      icon: "ri-alert-line",
      description: "Manage announcements",
    },
    {
      key: "mymenus",
      name: "My Menus",
      icon: "ri-book-read-line",
      description: "Manage personal menus and lists",
    },
    {
      key: "myspaces",
      name: "My Spaces",
      icon: "ri-home-gear-line",
      description: "Manage personal spaces and layouts",
    },
    {
      key: "setting",
      name: "Settings",
      icon: "ri-settings-3-line",
      description: "Access system settings",
    },
  ];

  // Permission levels with colors
  const permissionLevels = [
    { value: 0, label: "No Access", color: "danger", description: "Cannot view or edit" },
    { value: 1, label: "View Only", color: "warning", description: "Can view but not edit" },
    { value: 2, label: "Full Access", color: "success", description: "Can view and edit" },
  ];

  useEffect(() => {
    // Check if current user is ROOT
    const checkRootStatus = async () => {
      const user = await tokenHelper.getUser();
      setIsRoot(user?.role === "ROOT");
    };
    checkRootStatus();

    // Load existing permissions if available
    if (rowData?.permission) {
      setPermissions({
        kitchen: rowData.permission.kitchen || 0,
        site: rowData.permission.site || 0,
        space: rowData.permission.space || 0,
        menu: rowData.permission.menu || 0,
        menu_item: rowData.permission.menu_item || 0,
        device: rowData.permission.device || 0,
        order: rowData.permission.order || 0,
        delivery_time_report: rowData.permission.delivery_time_report || 0,
        service_station_report: rowData.permission.service_station_report || 0,
        feedback: rowData.permission.feedback || 0,
        link: rowData.permission.link || 0,
        setting: rowData.permission.setting || 0,
        announcements: rowData.permission.announcements || 0,
        mymenus: rowData.permission.mymenus || 0,
        myspaces: rowData.permission.myspaces || 0,
      });
    }
  }, [rowData]);

  const handlePermissionChange = (feature, level) => {
    setPermissions((prev) => ({
      ...prev,
      [feature]: level,
    }));
  };

  const handleSubmit = async () => {
    try {
      await updateUserPermissions(rowData.user_id, permissions);
      togglePermissionsModal();
    } catch (error) {
      console.error("Error updating permissions:", error);
    }
  };

  const getLevelColor = (level) => {
    return permissionLevels[level]?.color || "secondary";
  };

  const getLevelLabel = (level) => {
    return permissionLevels[level]?.label || "Unknown";
  };

  if (!isRoot) {
    return (
      <Modal isOpen={showPermissionsModal} toggle={togglePermissionsModal}>
        <ModalHeader toggle={togglePermissionsModal}>
          <i className="ri-lock-line me-2"></i>
          {t("Access Denied")}
        </ModalHeader>
        <ModalBody>
          <Alert color="danger">
            <i className="ri-error-warning-line me-2"></i>
            Only ROOT users can modify permissions.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={togglePermissionsModal}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={showPermissionsModal} toggle={togglePermissionsModal} size="lg">
      <ModalHeader toggle={togglePermissionsModal}>
        <i className="ri-shield-user-line me-2"></i>
        {t("Edit User Permissions")}
      </ModalHeader>
      <ModalBody>
        <UserInfoCard>
          <div className="d-flex align-items-center gap-3 mb-4">
            {rowData?.image_url ? (
              <img
                src={rowData.image_url}
                alt={rowData.name}
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "bold",
                }}
              >
                {rowData?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
            )}
            <div>
              <h5 className="mb-1">{rowData?.name}</h5>
              <p className="text-muted mb-1">{rowData?.email}</p>
              <Badge color="info">{rowData?.role}</Badge>
            </div>
          </div>
        </UserInfoCard>

        <PermissionsGrid>
          {features.map((feature) => (
            <PermissionCard key={feature.key}>
              <div className="d-flex align-items-center justify-content-between w-100 gap-3">
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <FeatureIcon className={feature.icon} />
                  <div>
                    <h6 className="mb-0">{feature.name}</h6>
                    <small className="text-muted">{feature.description}</small>
                  </div>
                </div>
                <div className="permission-levels">
                  {permissionLevels.map((level) => (
                    <LevelButton
                      key={level.value}
                      type="button"
                      className={`level-btn ${permissions[feature.key] === level.value ? "active" : ""
                        }`}
                      $color={level.color}
                      $isActive={permissions[feature.key] === level.value}
                      onClick={() => handlePermissionChange(feature.key, level.value)}
                      title={level.description}
                    >
                      <i
                        className={`ri-${level.value === 0
                          ? "close-circle-line"
                          : level.value === 1
                            ? "eye-line"
                            : "checkbox-circle-line"
                          }`}
                      ></i>
                      <span>{level.label}</span>
                    </LevelButton>
                  ))}
                </div>
              </div>
            </PermissionCard>
          ))}
        </PermissionsGrid>

        <PermissionLegend>
          <h6 className="mb-3">
            <i className="ri-information-line me-2"></i>
            Permission Levels
          </h6>
          <div className="d-flex flex-wrap gap-3">
            {permissionLevels.map((level) => (
              <div key={level.value} className="d-flex align-items-center gap-2">
                <Badge color={level.color}>{level.label}</Badge>
                <span className="text-muted" style={{ fontSize: "13px" }}>
                  {level.description}
                </span>
              </div>
            ))}
          </div>
        </PermissionLegend>
      </ModalBody>
      <ModalFooter>
        <Button
          color="success"
          onClick={handleSubmit}
          disabled={isLoading}
          className="d-flex align-items-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" />
              <span>{t("Saving...")}</span>
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              <span>{t("Save Permissions")}</span>
            </>
          )}
        </Button>
        <Button
          color="secondary"
          onClick={togglePermissionsModal}
          disabled={isLoading}
        >
          {t("Cancel")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditPermissions;

// Styled Components
const UserInfoCard = styled.div`
  background: linear-gradient(135deg, #ce9558 0%, #F3DEC8 100%);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  color: white;

  h5 {
    color: white;
    font-weight: 600;
  }

  .text-muted {
    color: rgba(255, 255, 255, 0.8) !important;
  }
`;

const PermissionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const PermissionCard = styled.div`
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.3s ease;
  background: white;
  width: 100%;

  &:hover {
    border-color: #ce9558;
    box-shadow: 0 4px 12px rgba(206, 149, 88, 0.1);
  }

  .permission-levels {
    display: flex;
    flex-direction: row;
    gap: 8px;
    flex-wrap: nowrap;
  }
`;

const FeatureIcon = styled.i`
  font-size: 20px;
  color: #ce9558;
  min-width: 24px;
`;

const LevelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  min-width: 110px;
  border: 2px solid
    ${(props) =>
    props.$isActive
      ? props.$color === "success"
        ? "#28a745"
        : props.$color === "warning"
          ? "#ffc107"
          : "#dc3545"
      : "#e9ecef"};
  background: ${(props) =>
    props.$isActive
      ? props.$color === "success"
        ? "rgba(40, 167, 69, 0.1)"
        : props.$color === "warning"
          ? "rgba(255, 193, 7, 0.1)"
          : "rgba(220, 53, 69, 0.1)"
      : "white"};
  color: ${(props) =>
    props.$isActive
      ? props.$color === "success"
        ? "#28a745"
        : props.$color === "warning"
          ? "#d39e00"
          : "#dc3545"
      : "#6c757d"};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: ${(props) => (props.$isActive ? "600" : "400")};

  i {
    font-size: 16px;
  }

  &:hover {
    border-color: ${(props) =>
    props.$color === "success"
      ? "#28a745"
      : props.$color === "warning"
        ? "#ffc107"
        : "#dc3545"};
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PermissionLegend = styled.div`
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #ce9558;

  h6 {
    color: #495057;
    font-weight: 600;
  }
`;

